<template>
  <transition :name="direction">
    <div
      class="sidepanel"
      v-if="panel"
      :style="calculateOffset"
      :left="true"
      :key="id"
      :class="{
        processing: processing,
        noscroll: noscroll,
        wide: wide,
        xwide: xwide
      }"
    >
      <div
        class="sidepanel-wrapper"
        :style="{ 'background-color': backgroundColor }"
      >
        <transition name="processing">
          <div class="processing-info" v-if="processing">
            <loading-spinner />
          </div>
        </transition>
        <div class="sidepanel-header" :style="headerStyle">
          <span>{{ title }}</span>
          <button @click="done(id, false)">
            <v-icon name="times" />
          </button>
          <button
            class=""
            @click="save ? save() : $emit('save')"
            v-if="save || saveIcon"
            id="sidepanel-save-icon"
          >
            <v-icon name="check" />
          </button>
        </div>
        <div class="sidepanel-contents" :class="{ nopad: nopadding }">
          <slot :payload="panel.payload"></slot>
        </div>
        <div class="sidepanel-actions" v-if="doneButton">
          <button @click="done(id, true)">Done</button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: "sidepanel",
  props: {
    title: {
      type: String
    },
    id: {
      type: String
    },
    left: {
      type: Boolean
    },
    doneButton: {
      type: Boolean
    },
    save: {
      type: Function,
      required: false
    },
    saveIcon: {
      type: Boolean,
      default: false,
      required: false
    },
    colorFG: {
      type: String
    },
    colorBG: {
      type: String
    },
    processing: {
      type: Boolean
    },
    nopadding: {
      type: Boolean,
      required: false,
      default: false
    },
    wide: {
      type: Boolean,
      required: false,
      default: false
    },
    xwide: {
      type: Boolean,
      required: false,
      default: false
    },
    noscroll: {
      type: Boolean,
      required: false,
      default: false
    },
    backgroundColor: {
      type: String,
      required: false,
      default: "#ffffff"
    }
  },
  computed: {
    direction: function() {
      if (!this.left) return "sidepanel";
      return "sidepanel-left";
    },
    calculateOffset: function() {
      if (!this.left) return { right: 5 * this.panelDepth + "px" };
      return { left: "0" };
    },
    panelDepth: function() {
      return this.$store.getters["panelDepth"](this.id);
    },
    panel: function() {
      return this.$store.getters["sidepanel"](this.id);
    },
    headerStyle() {
      let styleObj = {};
      if (this.colorBG) styleObj["background-color"] = this.colorBG;
      if (this.colorFG) styleObj["color"] = this.colorFG;
      return styleObj;
    }
  },
  methods: {
    done(id, doneButton) {
      this.$emit("done", doneButton);
      this.removePanel();
    }
  },
  watch: {
    panel: {
      handler(to, from) {
        const fromId = from ? from.id : "";
        if (to !== null && to.id !== fromId) this.$emit("open");
        if (to === null) this.$emit("close");
      }
    }
  }
};
</script>

<style lang="less">
@import "../styles/vars";

.sidepanel {
  position: fixed;
  width: 100%;
  background-color: #fff;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 300;
  box-shadow: 0 0 5px rgba(0, 0, 0, 0.2);
  transition: right 0.2s ease-out;
  .processing-info {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    right: 0;
    background-color: rgba(255, 255, 255, 0.75);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 10;
  }
  .sidepanel-wrapper {
    display: flex;
    flex-direction: column;
    position: relative;
    height: 100%;
  }
  .sidepanel-header {
    flex: 0 @h6;
    display: flex;
    align-items: center;
    background-color: #333;
    color: #fff;
    z-index: 0;
    span {
      flex: 1;
      display: flex;
      height: 100%;
      width: 100%;
      align-items: center;
      padding-left: @h2;
      font-weight: bold;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    button {
      flex: 0 @h6;
      height: @h6;
      background-color: transparent;
      font: inherit;
      color: inherit;
      border: none;
      appearance: none;
      border-radius: 0;
      &#sidepanel-save-icon {
        background-color: rgba(0, 0, 0, 0.1);
      }
    }
  }

  .panel-object {
    padding: @h1;
    background-color: #eee;
    margin: @h1 -@h1 @h1 -@h1;
    &:first-child {
      margin-top: -@h1;
    }
    & > div {
      &:not(.no-object-padding) {
        padding: @h1 * 1.5;
      }
      border-radius: @hh;
      background-color: #fff;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      // background-color: @colGreyLight;
      color: #333; // fade(@colBrandBlack, 50%);
    }
    &.white {
      background-color: #fff;
      & > div {
        box-shadow: 0 1px 5px rgba(0, 0, 0, 0.15);
      }
    }
  }

  &:not(.noscroll) {
    .sidepanel-contents {
      .scrollable();
    }
  }
  &.noscroll {
    .sidepanel-contents {
      overflow: hidden;
      position: relative;
    }
  }

  .fill-panel {
    width: 100%;
    height: 100%;
  }

  .sidepanel-contents {
    flex: 1;
    &:not(.nopad) {
      padding: @h1;
    }
    .no-padding {
      margin: -@h1;
    }

    .panel-note {
      margin: -@h1;
      margin-bottom: @h1;
      padding: @h2;
      background-color: #ffffcc;
    }
  }
}

@media @medium {
  .sidepanel {
    width: @panelWidth;
    max-width: 100%;
    &.wide {
      width: 1.5 * @panelWidth;
    }
    &.xwide {
      width: 3 * @panelWidth;
    }
  }
}

.sidepanel-enter-active,
.sidepanel-leave-active {
  transition: all 0.2s ease-out;
}
.sidepanel-enter,
.sidepanel-leave-to {
  opacity: 0.5;
  transform: translateX(100%);
}
.sidepanel-left-enter-active,
.sidepanel-left-leave-active {
  transition: all 0.2s ease-out;
}
.sidepanel-left-enter,
.sidepanel-left-leave-to {
  opacity: 0.5;
  transform: translateX(-100%);
}

.processing-enter-active,
.processing-leave-active {
  transition: all 0.2s ease-out;
}
.processing-enter,
.processing-leave-to {
  opacity: 0;
}
</style>
